/**
 * buddyPhraseLibrary — Structured BTC-maximalist phrase library.
 * Source of truth for Buddy's personality voice layer.
 * 
 * Used by the backend worker for footerLine selection,
 * event override lines, share card support, and weekly X post lines.
 * 
 * Tone: dry, calm, Bitcoin-first, slightly meme-aware, not loud.
 */

export type PhraseCategory =
  | "DEEP_VALUE"
  | "ACCUMULATION"
  | "NEUTRAL"
  | "WARM"
  | "HOT"
  | "MIXED"
  | "BLOCK_FOUND"
  | "MEMPOOL_JAM"
  | "WINTER"
  | "SUMMER"
  | "GENERAL_MAXI";

export type BuddyPhraseLibrary = Record<PhraseCategory, string[]>;

export const PHRASE_LIBRARY: BuddyPhraseLibrary = {
  DEEP_VALUE: [
    "Stay humble.",
    "Cheap sats.",
    "Still here.",
    "Bitcoin does not care.",
    "Quiet conviction.",
    "Time does the work.",
    "Stack gently.",
    "Low vibes. High signal.",
    "Bears are temporary.",
    "Strong hands get silence first.",
    "Proof of work. Proof of patience.",
    "Cold is where conviction lives.",
    "Price down. Thesis intact.",
    "Nobody likes it. Good.",
    "This is where believers are made.",
  ],

  ACCUMULATION: [
    "Tick tock.",
    "Build quietly.",
    "Early is boring.",
    "The thaw begins.",
    "Patience pays first.",
    "Before it gets crowded.",
    "Stack before the speeches.",
    "The chain is waking up.",
    "Calm accumulation hours.",
    "Still cheap enough to think.",
    "Conviction likes quiet weather.",
    "This is not the exciting part.",
    "Good. Let them sleep.",
    "Slow is fine.",
    "No rush. Just sats.",
  ],

  NEUTRAL: [
    "No strong edge.",
    "Wait and watch.",
    "Bitcoin is not in a hurry.",
    "Let the market speak.",
    "Observe first.",
    "No rush.",
    "Flat is still information.",
    "Patience is a position.",
    "Not every week is a gift.",
    "Signals need time.",
    "No need to force it.",
    "The chain remains operational.",
    "This is called uncertainty.",
    "Quiet tape. Open eyes.",
    "Not bearish. Not eager.",
  ],

  WARM: [
    "It's getting crowded.",
    "Discipline matters.",
    "Don't outsource conviction.",
    "Strong hands, clear head.",
    "Momentum is not a thesis.",
    "Better late than emotional.",
    "Crowds are not a strategy.",
    "Good market. Worse entry.",
    "Excitement is getting expensive.",
    "Still Bitcoin. Still risky.",
    "Green candles test discipline too.",
    "Up only is not a plan.",
    "The crowd has arrived.",
    "Price is louder now.",
    "Stay cooler than the market.",
  ],

  HOT: [
    "Suspiciously sunny.",
    "This seems sustainable.",
    "Euphoria is not a thesis.",
    "Top vibes.",
    "Too easy is information.",
    "This is where discipline earns.",
    "Everyone is a genius again.",
    "Hot money. Cold truth.",
    "Don't confuse heat with value.",
    "The market is feeling itself.",
    "Great vibes. Expensive sats.",
    "Risk is back in fashion.",
    "The chart looks loud.",
    "Stay humble, especially now.",
    "This is not where conviction begins.",
  ],

  MIXED: [
    "Mixed weather.",
    "Let it resolve.",
    "Not all signals agree.",
    "Unclear is still a signal.",
    "Confusion is not conviction.",
    "Wait for cleaner skies.",
    "The market is talking in riddles.",
    "One step at a time.",
    "No clean read.",
    "Maybe later.",
    "Bitcoin does not reward rushing.",
    "Some green, some fog.",
    "Not enough clarity yet.",
    "Watch first. Act later.",
    "Let the tape settle.",
  ],

  BLOCK_FOUND: [
    "Morale improved slightly.",
    "Block found. Breathing resumes.",
    "Relief event detected.",
    "The chain exhales.",
    "One more block. One less problem.",
    "Progress, as promised.",
    "We are so back, briefly.",
    "Order restored.",
    "Hashes did their job.",
    "Another block in the cathedral.",
    "The machine keeps moving.",
    "Security just happened.",
    "Proof of work never sleeps.",
    "The queue forgives.",
    "Calm returns to the island.",
  ],

  MEMPOOL_JAM: [
    "Queue spiritually unresolved.",
    "Waiting professionally.",
    "The jam persists.",
    "Pressure remains.",
    "Still no block.",
    "Congestion has opinions.",
    "Patience under load.",
    "The queue grows teeth.",
    "This is why fees exist.",
    "Busy chain, expensive attention.",
    "Stress test season.",
    "Demand is real.",
    "The block space is spoken for.",
    "Tick slower.",
    "Calm mind, crowded mempool.",
  ],

  WINTER: [
    "Building season.",
    "Bears outside. Builders inside.",
    "Surviving gracefully.",
    "Frozen mood. Live chain.",
    "Winter is for conviction.",
    "Nobody's cheering. Good.",
    "The tourists left.",
    "Silence is a feature.",
    "This is the real market.",
    "Strong hands get winter.",
    "Hope is not the strategy. Patience is.",
    "Cold air. Clear thesis.",
    "The weak story is gone.",
    "Bitcoin survives mood swings.",
    "This is where signal returns.",
  ],

  SUMMER: [
    "Vitamin D and green candles.",
    "Beach weather for bad decisions.",
    "NGMI cancelled, apparently.",
    "Sunshine with warning labels.",
    "Bull mood detected.",
    "The market found confidence again.",
    "Too much optimism in the air.",
    "Feels great. Stay careful.",
    "Summer on the island.",
    "Good times get loud.",
    "Warm weather, weaker discipline.",
    "This is how FOMO dresses.",
    "The vibes are expensive.",
    "Bright sky. Brighter risk.",
    "Everyone loves Bitcoin again.",
  ],

  GENERAL_MAXI: [
    "No second best.",
    "Stay humble.",
    "Tick tock.",
    "Bitcoin does not care.",
    "Proof of work, proof of patience.",
    "Time in the market.",
    "Stack gently.",
    "Hard money, soft voice.",
    "Signal over noise.",
    "The thesis remains.",
    "Sats over feelings.",
    "Conviction over chatter.",
    "Fiat feelings. Bitcoin rails.",
    "The clock keeps ticking.",
    "One chain. No drama.",
  ],
};

// ── Phrase Selection Helpers ──

/** Deterministic daily hash from a date string */
function dailyHash(dateStr: string): number {
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    hash = (hash * 31 + dateStr.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** Pick a stable phrase from a pool using a seed */
function pickFromPool(pool: string[], seed: number): string {
  if (pool.length === 0) return "";
  return pool[seed % pool.length];
}

/**
 * Get the daily footer line based on macro state, season, and event context.
 * Rotates at most once per day unless an event override is active.
 */
export function getDailyFooterLine(opts: {
  macroState: string;
  seasonState?: string;
  dateStr?: string;
}): string {
  const { macroState, seasonState, dateStr } = opts;
  const d = dateStr ?? new Date().toISOString().slice(0, 10);
  const seed = dailyHash(d);

  // Primary pool: macro state
  const macroPool = PHRASE_LIBRARY[macroState as PhraseCategory] ?? PHRASE_LIBRARY.NEUTRAL;

  // Season blend: ~30% chance to use seasonal phrase
  if (seasonState === "cold" || seasonState === "WINTER") {
    if (seed % 10 < 3) return pickFromPool(PHRASE_LIBRARY.WINTER, seed);
  }
  if (seasonState === "heat" || seasonState === "HEAT" || seasonState === "SUMMER") {
    if (seed % 10 < 3) return pickFromPool(PHRASE_LIBRARY.SUMMER, seed);
  }

  return pickFromPool(macroPool, seed);
}

/**
 * Get an event-driven override line (block found, mempool jam).
 * Returns null if no override applies.
 */
export function getEventOverrideLine(opts: {
  blockJustFound?: boolean;
  isMempoolJam?: boolean;
  seed?: number;
}): string | null {
  const { blockJustFound, isMempoolJam, seed = Date.now() } = opts;

  if (blockJustFound) {
    return pickFromPool(PHRASE_LIBRARY.BLOCK_FOUND, seed);
  }
  if (isMempoolJam) {
    return pickFromPool(PHRASE_LIBRARY.MEMPOOL_JAM, seed);
  }
  return null;
}

/**
 * Get a support line for share cards.
 * Uses macro state + general maxi blend.
 */
export function getShareSupportLine(opts: {
  macroState: string;
  seed?: number;
}): string {
  const { macroState, seed = Date.now() } = opts;
  // Alternate between macro-specific and general maxi
  if (seed % 3 === 0) {
    return pickFromPool(PHRASE_LIBRARY.GENERAL_MAXI, seed);
  }
  const pool = PHRASE_LIBRARY[macroState as PhraseCategory] ?? PHRASE_LIBRARY.NEUTRAL;
  return pickFromPool(pool, seed);
}

/**
 * Get a weekly X post closing line.
 * Deterministic per ISO week.
 */
export function getWeeklyXClosingLine(opts: {
  macroState: string;
  dateStr?: string;
}): string {
  const { macroState, dateStr } = opts;
  const d = dateStr ?? new Date().toISOString().slice(0, 10);
  // Use week-level seed (divide day-of-year by 7)
  const weekSeed = Math.floor(dailyHash(d) / 7);
  // 50/50 macro vs general
  if (weekSeed % 2 === 0) {
    return pickFromPool(PHRASE_LIBRARY.GENERAL_MAXI, weekSeed);
  }
  const pool = PHRASE_LIBRARY[macroState as PhraseCategory] ?? PHRASE_LIBRARY.NEUTRAL;
  return pickFromPool(pool, weekSeed);
}
